import React, { useEffect, useMemo, useState } from 'react';
import VendorService from '../../services/VendorService';
import utility from '../../utils/utility';
import ReactPaginate from 'react-paginate';
import FilterBar from '../../components/FilterBar';
import { filterList } from '../../utils/filterUtils';

const Vendor = () => {
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [pageCount, setPageCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;
    const [searchText, setSearchText] = useState('');
    const [formData, setFormData] = useState({
        id: '',
        vendorName: '',
        vendorType: '',
        contactPersonName: '',
        youtubeLink: '',
        instaLink: '',
        facebookLink: '',
        website: '',
        status: 'Active',
        isActive: 1
    });
    const filteredVendors = useMemo(() => filterList(vendors, searchText, ['vendorName', 'vendorType', 'contactPersonName', 'status', 'id']), [vendors, searchText]);

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        fetchData();
    }, [currentPage]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const data = await VendorService.getAll(currentPage, itemsPerPage);
            setVendors(data.data || []);
            setPageCount(data.pagination.totalPages || 0);
        } catch (err) {
            utility.showError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (vendor = null) => {
        if (vendor) {
            setEditMode(true);
            setFormData({
                id: vendor.id,
                vendorName: vendor.vendorName,
                vendorType: vendor.vendorType || '',
                contactPersonName: vendor.contactPersonName || '',
                youtubeLink: vendor.youtubeLink || '',
                instaLink: vendor.instaLink || '',
                facebookLink: vendor.facebookLink || '',
                website: vendor.website || '',
                status: vendor.status || 'Active',
                isActive: vendor.isActive
            });
        } else {
            setEditMode(false);
            setFormData({
                id: '',
                vendorName: '',
                vendorType: '',
                contactPersonName: '',
                youtubeLink: '',
                instaLink: '',
                facebookLink: '',
                website: '',
                status: 'Active',
                isActive: 1
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: name === 'isActive' ? parseInt(value) : value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            if (editMode) {
                await VendorService.update(formData.id, formData);
                utility.showSuccess('Vendor updated successfully');
            } else {
                await VendorService.create(formData);
                utility.showSuccess('Vendor created successfully');
            }
            handleCloseModal();
            fetchData();
        } catch (err) {
            utility.showError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleToggleStatus = async (vendor) => {
        try {
            const newStatus = vendor.isActive ? 0 : 1;
            await VendorService.updateStatus(vendor.id, newStatus);
            utility.showSuccess(`Vendor status ${newStatus ? 'activated' : 'deactivated'} successfully`);
            fetchData();
        } catch (err) {
            utility.showError(err.message);
        }
    };

    const handlePageClick = (event) => {
        const selectedPage = event.selected + 1;
        setCurrentPage(selectedPage);
    };

    return (
        <div className="container-fluid py-2">
            <div className="row">
                <div className="col-12 text-end mb-3">
                    <button className="btn bg-gradient-dark mb-0" onClick={() => handleOpenModal()}>
                        <i className="material-symbols-rounded text-sm">add</i>&nbsp;&nbsp;Add New Vendor
                    </button>
                </div>
                <div className="col-12">
                    <div className="card my-4">
                        <div className="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
                            <div className="bg-gradient-dark shadow-dark border-radius-lg pt-4 pb-3">
                                <h6 className="text-white text-capitalize ps-3">Vendors</h6>
                            </div>
                        </div>
                        <div className="card-body px-0 pb-2">
                            {/* Error alert removed in favor of professional toasts */}
                            <div className="px-3">
                                <FilterBar
                                    value={searchText}
                                    onChange={setSearchText}
                                    onClear={() => setSearchText('')}
                                    placeholder="Search vendors..."
                                />
                            </div>
                            <div className="table-responsive p-0">
                                {loading ? (
                                    <div className="p-4 text-center">Loading...</div>
                                ) : (
                                    <table className="table align-items-center mb-0">
                                        <thead>
                                            <tr>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Vendor Name</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Type</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Contact Person</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Status</th>
                                                <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredVendors.length === 0 ? (
                                                <tr><td colSpan="5" className="text-center py-4">No vendors found.</td></tr>
                                            ) : (
                                                filteredVendors.map((vendor) => (
                                                    <tr key={vendor.id}>
                                                        <td>
                                                            <div className="d-flex px-2 py-1">
                                                                <div className="d-flex flex-column justify-content-center">
                                                                    <h6 className="mb-0 text-sm">{vendor.vendorName}</h6>
                                                                    <p className="text-xs text-secondary mb-0">{vendor.website || 'No Website'}</p>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0">{vendor.vendorType || 'N/A'}</p>
                                                        </td>
                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0">{vendor.contactPersonName || 'N/A'}</p>
                                                        </td>
                                                        <td>
                                                            <span
                                                                className={`badge badge-sm bg-gradient-${vendor.isActive ? 'success' : 'secondary'} cursor-pointer`}
                                                                onClick={() => handleToggleStatus(vendor)}
                                                            >
                                                                {vendor.isActive ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </td>
                                                        <td className="align-middle text-center">
                                                            <button className="btn btn-link text-info mb-0" onClick={() => handleOpenModal(vendor)}>
                                                                Edit
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colSpan="5">
                                                    <ReactPaginate
                                                        forcePage={currentPage - 1}
                                                        breakLabel="..."
                                                        nextLabel=">"
                                                        onPageChange={handlePageClick}
                                                        pageRangeDisplayed={3}
                                                        marginPagesDisplayed={2}
                                                        pageCount={pageCount}
                                                        previousLabel="<"
                                                        containerClassName="pagination"
                                                        pageClassName="page-item"
                                                        pageLinkClassName="page-link"
                                                        previousClassName="page-item"
                                                        previousLinkClassName="page-link"
                                                        nextClassName="page-item"
                                                        nextLinkClassName="page-link"
                                                        activeClassName="active"
                                                        disabledClassName="disabled"
                                                        breakClassName="page-item"
                                                        breakLinkClassName="page-link"
                                                    />
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>


                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header border-0">
                                <h5 className="modal-title font-weight-normal">{editMode ? 'Edit Vendor' : 'Add New Vendor'}</h5>
                                <button type="button" className="btn-close text-dark" onClick={handleCloseModal}>
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body row">
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Vendor Name</label>
                                            <input type="text" name="vendorName" className="form-control" value={formData.vendorName} onChange={handleInputChange} required />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Vendor Type</label>
                                            <input type="text" name="vendorType" className="form-control" value={formData.vendorType} onChange={handleInputChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Contact Person Name</label>
                                            <input type="text" name="contactPersonName" className="form-control" value={formData.contactPersonName} onChange={handleInputChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Website</label>
                                            <input type="text" name="website" className="form-control" value={formData.website} onChange={handleInputChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>YouTube Link</label>
                                            <input type="text" name="youtubeLink" className="form-control" value={formData.youtubeLink} onChange={handleInputChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Instagram Link</label>
                                            <input type="text" name="instaLink" className="form-control" value={formData.instaLink} onChange={handleInputChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Facebook Link</label>
                                            <input type="text" name="facebookLink" className="form-control" value={formData.facebookLink} onChange={handleInputChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Status Label</label>
                                            <input type="text" name="status" className="form-control" value={formData.status} onChange={handleInputChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Is Active</label>
                                            <select name="isActive" className="form-control" value={formData.isActive} onChange={handleInputChange}>
                                                <option value={1}>Active</option>
                                                <option value={0}>Inactive</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer border-0">
                                    <button type="button" className="btn bg-gradient-secondary" onClick={handleCloseModal} disabled={saving}>Cancel</button>
                                    <button type="submit" className="btn bg-gradient-dark" disabled={saving}>
                                        {saving ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                {editMode ? 'Updating...' : 'Saving...'}
                                            </>
                                        ) : (
                                            editMode ? 'Update' : 'Save'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Vendor;
